import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import InputFieldNumber from '../../../common/inputs/InputFieldNumber';
import { LeaseTier, LeaseTierCreate, LeaseTierUpdate } from '../../../../types';
import { colors } from '../../../../styles/colors';

interface LeaseTiersEditorProps {
  /** Liste der Pachtkostenstaffeln */
  leaseTiers: LeaseTier[];
  /** Projektlaufzeit in Jahren (bestimmt das letzte Jahr der letzten Staffel) */
  projektLaufzeit: number;
  /** Ob Bearbeitung erlaubt ist */
  canEdit: boolean;
  /** Callback wenn eine Staffel geändert wird */
  onTierChange: (tierId: string, data: LeaseTierUpdate) => void;
  /** Callback wenn eine neue Staffel hinzugefügt wird */
  onTierAdd: (data: LeaseTierCreate) => void;
  /** Callback wenn eine Staffel gelöscht wird */
  onTierDelete: (tierId: string) => void;
  /** Berechneter Basiswert in EUR (für Jahr 1, vom Backend) - zur Berechnung des Jahresertrags */
  basiswert?: number;
}

/**
 * Berechnet das letzte Jahr für eine Staffel basierend auf der nächsten Staffel
 * oder der Projektlaufzeit wenn es die letzte Staffel ist
 */
const calculateLastYear = (
  tier: LeaseTier,
  index: number,
  allTiers: LeaseTier[],
  projektLaufzeit: number
): number => {
  if (index < allTiers.length - 1) {
    // Nicht die letzte Staffel: Ende = nächste.first_year - 1
    return allTiers[index + 1].first_year - 1;
  }
  // Letzte Staffel: Ende = Projektlaufzeit
  return projektLaufzeit;
};

/**
 * Staffel-Bezeichnung mit integriertem Startjahr
 * Format: "Von Jahr X bis Jahr Y" wobei X ab Zeile 2 als Eingabefeld angezeigt wird
 */
interface TierLabelProps {
  firstYear: number;
  lastYear: number;
  isFirstTier: boolean;
  canEdit: boolean;
  onFirstYearChange: (value: number) => void;
  projektLaufzeit: number;
}

const TierLabel: React.FC<TierLabelProps> = ({
  firstYear,
  lastYear,
  isFirstTier,
  canEdit,
  onFirstYearChange,
  projektLaufzeit,
}) => {
  const canEditFirstYear = !isFirstTier && canEdit;

  const handleFirstYearChange = (value: string) => {
    const num = parseInt(value, 10);
    if (!isNaN(num) && num >= 1 && num <= projektLaufzeit) {
      onFirstYearChange(num);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 200, gap: 0.5 }}>
      <Typography component="span">Von Jahr</Typography>
      {/* Startjahr - immer als Feld, ab Zeile 2 editierbar */}
      <Box sx={{ width: 50 }}>
        <InputFieldNumber
          label=""
          value={String(firstYear)}
          onChange={handleFirstYearChange}
          readOnly={!canEditFirstYear}
          allowDecimals={false}
        />
      </Box>
      <Typography component="span">bis Jahr {lastYear}</Typography>
    </Box>
  );
};

/**
 * Einzelne Staffel-Zeile im gleichen Design wie OpexRow
 */
interface LeaseTierRowProps {
  tier: LeaseTier;
  index: number;
  lastYear: number;
  eurValue: number;
  canEdit: boolean;
  isFirstTier: boolean;
  projektLaufzeit: number;
  onTierChange: (tierId: string, data: LeaseTierUpdate) => void;
  onTierDelete: (tierId: string) => void;
}

const LeaseTierRow: React.FC<LeaseTierRowProps> = ({
  tier,
  lastYear,
  eurValue,
  canEdit,
  isFirstTier,
  projektLaufzeit,
  onTierChange,
  onTierDelete,
}) => {
  const handleFirstYearChange = (value: number) => {
    onTierChange(tier.id, { first_year: value });
  };

  const handlePercentageChange = (value: string) => {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      onTierChange(tier.id, { percentage: num });
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      py: 0.5,
      ml: -6, // Bündig mit anderen OPEX-Positionen
    }}>
      {/* Minus-Icon - ganz links, nicht bei erster Staffel */}
      <Box sx={{ width: 40, display: 'flex', justifyContent: 'center' }}>
        {!isFirstTier && canEdit ? (
          <Tooltip title="Staffel entfernen">
            <IconButton
              size="small"
              onClick={() => onTierDelete(tier.id)}
              sx={{
                color: colors.icon.subtle,
                '&:hover': { color: 'error.main' }
              }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          // Platzhalter für erste Staffel
          (<Box sx={{ width: 40 }} />)
        )}
      </Box>
      {/* Staffel-Bezeichnung "Von Jahr X bis Jahr Y" */}
      <TierLabel
        firstYear={tier.first_year}
        lastYear={lastYear}
        isFirstTier={isFirstTier}
        canEdit={canEdit}
        onFirstYearChange={handleFirstYearChange}
        projektLaufzeit={projektLaufzeit}
      />
      {/* Prozentsatz */}
      <Tooltip title="Anteil vom Erlös" placement="top" arrow>
        <Box sx={{ width: 100 }}>
          <InputFieldNumber
            label=""
            value={String(tier.percentage)}
            onChange={handlePercentageChange}
            readOnly={!canEdit}
            suffix="%"
          />
        </Box>
      </Tooltip>
      {/* EUR-Wert (berechnet, readonly) */}
      <Tooltip title="Pachtkosten pro Jahr" placement="top" arrow>
        <Box sx={{ width: 150, display: 'flex', justifyContent: 'flex-end' }}>
          <InputFieldNumber
            label=""
            value={String(Math.round(eurValue))}
            readOnly={true}
            suffix="€"
          />
        </Box>
      </Tooltip>
    </Box>
  );
};

/**
 * LeaseTiersEditor - Komponente zur Verwaltung von Pachtkostenstaffeln
 * 
 * Design angepasst an andere OPEX-Positionen:
 * - Minus-Icon links (außer erste Staffel)
 * - Bezeichnung "Jahr X-Y" mit editierbarem Startjahr ab Zeile 2
 * - Prozentsatz und EUR-Wert rechts
 * - Plus-Icon links zum Hinzufügen
 */
const LeaseTiersEditor: React.FC<LeaseTiersEditorProps> = ({
  leaseTiers,
  projektLaufzeit,
  canEdit,
  onTierChange,
  onTierAdd,
  onTierDelete,
  basiswert = 0,
}) => {
  // Berechne den Jahresertrag aus dem Basiswert und dem ersten Tier-Prozentsatz
  const firstTierPercentage = leaseTiers[0]?.percentage ?? 1;
  const jahresErtrag = firstTierPercentage > 0 ? (basiswert / firstTierPercentage) * 100 : 0;

  // Berechnet den EUR-Wert für eine Staffel basierend auf ihrem Prozentsatz
  const calculateEurValue = (percentage: number): number => {
    return (jahresErtrag * percentage) / 100;
  };

  const handleAddTier = () => {
    const lastTier = leaseTiers[leaseTiers.length - 1];

    if (!lastTier) {
      onTierAdd({ first_year: 1, percentage: 8 });
      return;
    }

    const lastTierFirstYear = lastTier.first_year;
    const lastTierLastYear = calculateLastYear(lastTier, leaseTiers.length - 1, leaseTiers, projektLaufzeit);

    // Neue Staffel beginnt in der Mitte der letzten Staffel
    const newFirstYear = Math.ceil((lastTierFirstYear + lastTierLastYear) / 2) + 1;
    const effectiveFirstYear = Math.max(newFirstYear, lastTierFirstYear + 1);

    if (effectiveFirstYear <= projektLaufzeit) {
      onTierAdd({
        first_year: effectiveFirstYear,
        percentage: lastTier.percentage ?? 8,
      });
    }
  };

  // Prüfe ob neue Staffel hinzugefügt werden kann
  const canAddTier = () => {
    if (!canEdit) return false;
    if (leaseTiers.length === 0) return true;

    const lastTier = leaseTiers[leaseTiers.length - 1];
    const lastTierFirstYear = lastTier.first_year;
    const lastTierLastYear = calculateLastYear(lastTier, leaseTiers.length - 1, leaseTiers, projektLaufzeit);

    return (lastTierLastYear - lastTierFirstYear) >= 1;
  };

  return (
    <Box>
      {/* Staffel-Zeilen */}
      {leaseTiers.map((tier, index) => {
        const lastYear = calculateLastYear(tier, index, leaseTiers, projektLaufzeit);
        const isFirstTier = index === 0;
        const eurValue = calculateEurValue(tier.percentage);

        return (
          <LeaseTierRow
            key={tier.id}
            tier={tier}
            index={index}
            lastYear={lastYear}
            eurValue={eurValue}
            canEdit={canEdit}
            isFirstTier={isFirstTier}
            projektLaufzeit={projektLaufzeit}
            onTierChange={onTierChange}
            onTierDelete={onTierDelete}
          />
        );
      })}

      {/* Plus-Icon zum Hinzufügen - bündig mit Minus-Icons */}
      {canAddTier() && (
        <Box sx={{ mt: 1, ml: -6, display: 'flex' }}>
          <Box sx={{ width: 40, display: 'flex', justifyContent: 'center' }}>
            <Tooltip title="Staffel anlegen">
              <IconButton
                size="small"
                onClick={handleAddTier}
                sx={{
                  color: 'primary.main',
                  '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' }
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* Hinweis wenn keine Staffeln vorhanden */}
      {leaseTiers.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', py: 1 }}>
          Keine Pachtkostenstaffeln vorhanden
        </Typography>
      )}
    </Box>
  );
};

export default LeaseTiersEditor;
